import { SheetPickerList } from '@/components/global/SheetPickerList';
import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
} from '@/components/ui/responsive-modal';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { useDebounce } from '@/hooks/use-debounce';
import { useTranslations } from '@/hooks/useTranslations';
import axios from '@/lib/axios';
import { useEffect, useRef, useState } from 'react';

export interface PickableCompany {
    id: string;
    name: string;
    company_code?: string | null;
}

interface CompanyPickerSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** The company currently on the record, so it is listed and marked even without a search hit. */
    value?: string | null;
    selected?: PickableCompany | null;
    onSelect: (company: PickableCompany | null) => void;
    /** Companies the caller already has, shown before the first search returns. */
    seedCompanies?: PickableCompany[];
    /** Offer a "no company" row — for an optional field such as the paying company. */
    allowClear?: boolean;
    title?: string;
    description?: string;
    clearLabel?: string;
}

/**
 * Picks one company from a list laid out in a sheet — the same shape as the
 * employee page's company assignment, rather than a combobox inside a card.
 * The search hits `/api/companies`, so the list is never limited to what the
 * page happened to load.
 */
export function CompanyPickerSheet({
    open,
    onOpenChange,
    value = null,
    selected = null,
    onSelect,
    seedCompanies = [],
    allowClear = false,
    title,
    description,
    clearLabel,
}: CompanyPickerSheetProps) {
    const { t } = useTranslations();
    const [searchTerm, setSearchTerm] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [options, setOptions] = useState<PickableCompany[]>([]);
    const [draft, setDraft] = useState<string | null>(value);

    const debouncedSearch = useDebounce(searchTerm, 300);
    // Kept in a ref so the current selection is merged into every result set
    // without making it a re-fetch trigger.
    const selectedRef = useRef<PickableCompany | null>(selected);
    useEffect(() => {
        selectedRef.current = selected;
    }, [selected]);

    // Opening starts from what is on the record; cancelling therefore changes nothing.
    useEffect(() => {
        if (open) {
            setDraft(value);
        } else {
            setSearchTerm('');
            setOptions([]);
        }
    }, [open, value]);

    useEffect(() => {
        if (!open) {
            return;
        }

        let cancelled = false;

        void (async () => {
            setIsSearching(true);

            try {
                const response = await axios.get('/api/companies', {
                    params: { search: debouncedSearch.trim() || undefined, limit: 25 },
                });

                if (cancelled) {
                    return;
                }

                const list = ((response.data?.companies ?? []) as Array<PickableCompany & { uuid?: string }>).map((company) => ({
                    id: String(company.id ?? company.uuid ?? ''),
                    name: company.name ?? '',
                    company_code: company.company_code ?? null,
                }));

                const byId = new Map<string, PickableCompany>(list.map((company) => [company.id, company] as const));
                [...seedCompanies, selectedRef.current].forEach((company) => {
                    if (company?.id && !byId.has(company.id)) {
                        byId.set(company.id, company);
                    }
                });

                setOptions(Array.from(byId.values()));
            } catch {
                // Leave whatever is on screen; the search can be retried.
            } finally {
                if (!cancelled) {
                    setIsSearching(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
        // seedCompanies is a prop array; the ids it carries are what matter.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, debouncedSearch]);

    const NO_COMPANY = '__none__';

    const handleConfirm = () => {
        if (!draft || draft === NO_COMPANY) {
            onSelect(null);
            onOpenChange(false);

            return;
        }

        onSelect(options.find((company) => company.id === draft) ?? selectedRef.current ?? null);
        onOpenChange(false);
    };

    return (
        <ResponsiveModal open={open} onOpenChange={onOpenChange} variant="sheet">
            <ResponsiveModalContent size="lg">
                <ResponsiveModalHeader>
                    <ResponsiveModalTitle>{title ?? t('common.company_select.placeholder', 'Select a company')}</ResponsiveModalTitle>
                    <ResponsiveModalDescription>
                        {description ?? t('common.company_select.sheet_description', 'Search by name or company code, then pick one.')}
                    </ResponsiveModalDescription>
                </ResponsiveModalHeader>

                <ResponsiveModalBody>
                    <SheetPickerList
                        value={draft}
                        onValueChange={setDraft}
                        search={searchTerm}
                        onSearchChange={setSearchTerm}
                        loading={isSearching && options.length === 0}
                        options={[
                            ...(allowClear
                                ? [
                                      {
                                          id: NO_COMPANY,
                                          label: clearLabel ?? t('common.company_select.none', 'No company'),
                                      },
                                  ]
                                : []),
                            ...options.map((company) => ({
                                id: company.id,
                                label: company.name,
                                keywords: company.company_code ?? undefined,
                                badge: company.company_code ? (
                                    <Badge variant="outline" className="text-xs">
                                        {company.company_code}
                                    </Badge>
                                ) : null,
                            })),
                        ]}
                        labels={{
                            search: t('common.company_select.search', 'Search by name or code…'),
                            empty: t('common.company_select.empty', 'No companies found.'),
                            loading: t('common.company_select.searching', 'Searching…'),
                        }}
                    />
                </ResponsiveModalBody>

                <ResponsiveModalFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        {t('common.cancel', 'Cancel')}
                    </Button>
                    <Button onClick={handleConfirm} disabled={!draft}>
                        {t('common.select', 'Select')}
                    </Button>
                </ResponsiveModalFooter>
            </ResponsiveModalContent>
        </ResponsiveModal>
    );
}
